import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export interface RoleSummary {
  id: string;
  code: string;
  name: string;
}

export interface UserRecord {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  status: "ACTIVE" | "INACTIVE" | "PENDING_VERIFICATION" | "LOCKED";
  roles: RoleSummary[];
  clientId: string | null;
  /** % de comisión sobre ventas del POS (tienda) — null = sin configurar. */
  commissionRate: string | null;
  /** Meta de venta mensual en $ (tienda) — null = sin configurar. */
  monthlyGoal: string | null;
  createdAt: string;
}

export interface ListUsersFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export function useUsers(filters: ListUsersFilters) {
  return useQuery({
    queryKey: ["users", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<UserRecord[]>>("/users", { params: filters });
      return data;
    },
  });
}

export interface CreateUserPayload {
  fullName: string;
  email: string;
  password: string;
  phone?: string;
  roleIds: string[];
  /** Vincula el usuario a un Client existente (rol de sistema CLIENTE, Portal del cliente — Fase 19). */
  clientId?: string;
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<UserRecord>>("/users", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UpdateUserPayload {
  id: string;
  fullName?: string;
  phone?: string;
  status?: string;
  roleIds?: string[];
  clientId?: string | null;
  commissionRate?: number | string | null;
  monthlyGoal?: number | string | null;
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateUserPayload) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<UserRecord>>(`/users/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/users/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}
